import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink, AlertCircle, Sparkles, TrendingUp, BarChartBig, ShieldCheck, Coins } from 'lucide-react';
import { AppLayout } from '@/components/layouts/AppLayout';
import { MobileNav } from '@/components/MobileNav'; // Assuming you want the standard layout

interface Token {
  token_name: string;
  symbol: string;
  quantity: number;
  contract_address: string;
  current_price_usd: number;
  total_value_usd: number;
  percentage_of_portfolio: number;
  logo_url: string;
  is_native: boolean;
}

interface PortfolioAnalysis {
  overall_health: string;
  risk_assessment: string;
  rebalance_recommendations: string[];
  summary: string;
}

interface PortfolioResponse {
  wallet_address: string;
  chain_id: string;
  total_value_usd: number;
  tokens: Token[];
  retrieved_at: string;
  portfolio_analysis: PortfolioAnalysis;
}

const commonChains = [
  { id: 'ethereum', name: 'Ethereum' },
  { id: 'bsc', name: 'BNB Smart Chain' },
  { id: 'polygon', name: 'Polygon (Matic)' },
  { id: 'solana', name: 'Solana' },
  { id: 'avalanche', name: 'Avalanche C-Chain' },
  { id: 'arbitrum', name: 'Arbitrum One' },
  { id: 'optimism', name: 'Optimism' },
];

const WalletAnalyzer: React.FC = () => {
  const isMobile = useIsMobile();
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [chainId, setChainId] = useState<string>(commonChains[0].id);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      setProgress(0);
      let currentProgress = 0;
      timer = setInterval(() => {
        currentProgress += 10;
        if (currentProgress > 100) {
          currentProgress = 100; 
        }
        setProgress(currentProgress);
        if (currentProgress === 100 && data) clearInterval(timer);
        if (currentProgress === 100 && !data) setProgress(90); // Hold at 90 if still loading
      }, 300);
    }
    return () => clearInterval(timer);
  }, [loading, data]);

  const handleAnalyze = async () => {
    if (!walletAddress || !chainId) {
      setError('Wallet address and chain ID are required.');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    setProgress(0);

    try {
      // For GET requests with query parameters, encode them in the function name
      const functionPath = `wallet-analyzer?wallet_address=${encodeURIComponent(walletAddress.trim())}&chain_id=${encodeURIComponent(chainId)}`;      
      
      const { data: responseData, error: functionError } = await supabase.functions.invoke(functionPath, {
        method: 'GET',
      });

      if (functionError) {
        console.error("Raw functionError object from Supabase invoke:", functionError);
        
        // Handle specific edge function errors
        if (functionError.message?.includes('Edge Function returned a non-2xx status code') || 
            functionError.name === 'FunctionsHttpError') {
          throw new Error('Our wallet analysis service is currently experiencing high demand. Please try again in a few minutes.');
        }
        
        const errorMessage = functionError.message || 'Unknown error from Supabase invoke (no message property)';
        throw new Error(errorMessage);
      }
      if (responseData.error) { // Check for errors from the external API
        throw new Error(responseData.error);
      }
      setData(responseData as PortfolioResponse);
      setProgress(100);
    } catch (e: unknown) {
      let errorMessage = 'An unexpected error occurred.';
      if (e instanceof Error) {
        if (e.message.includes('wallet analysis service is currently experiencing high demand')) {
          errorMessage = e.message; // Use the specific edge function error message
        } else if (e.message.includes('fetch')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
        } else if (e.message.includes('Invalid wallet address')) {
          errorMessage = 'Invalid wallet address format. Please check your address and try again.';
        } else if (e.message.includes('Unsupported chain')) {
          errorMessage = 'The selected blockchain is not supported. Please choose a different chain.';
        } else {
          errorMessage = e.message;
        }
      } else if (typeof e === 'string') {
        errorMessage = e;
      }
      setError(errorMessage);
      console.error("Analysis error:", e);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-3 py-4 md:p-6 max-w-6xl bg-gradient-to-br from-black via-slate-950 to-black min-h-[80vh]">
        <Card className="mb-4 overflow-hidden rounded-md border border-blue-700/30 shadow-lg bg-black/80 backdrop-blur-sm">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"></div>
          <CardHeader className="p-4 md:p-5 border-b border-border/10 bg-gradient-to-r from-slate-950 to-slate-900">
            <div className="flex items-center gap-2">
                <Coins className="h-8 w-8 text-primary" />
                <CardTitle className="text-xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">Wallet Portfolio Analyzer</CardTitle>
            </div>
            <CardDescription className="text-sm md:text-lg text-muted-foreground">
              Enter a wallet address and select a blockchain to get a Strykr AI-powered analysis of its token holdings.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-5 bg-gradient-to-br from-slate-950 to-slate-900/80">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6">
              <Input
                type="text"
                placeholder="Enter Wallet Address (e.g., 0x...)"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="md:col-span-2 h-12 text-base rounded-md"
                disabled={loading}
                spellCheck={false}
                autoComplete="off"
              />
              <Select value={chainId} onValueChange={setChainId} disabled={loading}>
                <SelectTrigger className="h-12 text-base rounded-md">
                  <SelectValue placeholder="Select Chain" />
                </SelectTrigger>
                <SelectContent>
                  {commonChains.map(chain => (
                    <SelectItem key={chain.id} value={chain.id}>{chain.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAnalyze} disabled={loading} size="lg" className="w-full text-lg py-3 rounded-md font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 transition-all border-0 shadow-lg shadow-blue-900/20">
              {loading ? 'Analyzing...' : 'Analyze Portfolio'}
            </Button>
            {loading && <Progress value={progress} className="w-full mt-4 h-2 rounded-full" />}
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
            <Card className="md:col-span-4 overflow-hidden rounded-md border border-blue-600/30 shadow-lg h-full bg-gradient-to-br from-slate-950 to-slate-900/80">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
              <CardHeader className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 p-4 md:p-5">
                <div className="flex items-center gap-3">
                    <BarChartBig className="h-6 w-6 md:h-7 md:w-7 text-blue-500"/>
                    <CardTitle className="text-xl md:text-2xl">Portfolio Overview</CardTitle>
                </div>
                <div className="flex items-center justify-between md:justify-end md:text-right mt-2 md:mt-0">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Value (USD)</p>
                      <p className="text-xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">${data.total_value_usd ? data.total_value_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</p>
                    </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-5 bg-gradient-to-br from-slate-950 to-slate-900/80">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="sm:col-span-2 mb-2">
                      <p className="font-medium mb-1">Wallet:</p>
                      <div className="font-mono break-all text-xs bg-blue-950/50 text-blue-200 p-2 rounded-md border border-blue-800/30">{data.wallet_address}</div>
                    </div>
                    <div className="flex items-center mb-1 gap-2">
                      <p className="font-medium min-w-[90px]">Chain:</p>
                      <p>{commonChains.find(c => c.id === data.chain_id)?.name || data.chain_id}</p>
                    </div>
                    <div className="flex items-center mb-1 gap-2">
                      <p className="font-medium min-w-[90px]">Retrieved:</p>
                      <p>{new Date(data.retrieved_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center mb-1 gap-2">
                      <p className="font-medium min-w-[90px]">Tokens:</p>
                      <p>{data.tokens.length}</p>
                    </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="md:col-span-12 overflow-hidden rounded-md border border-emerald-600/30 shadow-lg h-full bg-gradient-to-br from-slate-950 to-slate-900/80">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-green-500"></div>
                <CardHeader className="p-4 md:p-5 border-b border-border/10 bg-gradient-to-r from-slate-950 to-slate-900">
                    <div className="flex items-center gap-3">
                        <Sparkles className="h-6 w-6 md:h-7 md:w-7 text-purple-500" />
                        <CardTitle className="text-xl md:text-2xl">Strykr AI Analysis</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="p-4 md:p-6 space-y-4">
                    <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><ShieldCheck className="text-green-500 h-5 w-5 flex-shrink-0"/>Overall Health:</h3>
                        <p className="text-muted-foreground">{data.portfolio_analysis.overall_health}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><AlertCircle className="text-orange-500 h-5 w-5 flex-shrink-0"/>Risk Assessment:</h3>
                        <p className="text-muted-foreground">{data.portfolio_analysis.risk_assessment}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2"><TrendingUp className="text-teal-500 h-5 w-5 flex-shrink-0"/>Rebalance Recommendations:</h3>
                        {data.portfolio_analysis.rebalance_recommendations.length > 0 ? (
                            <ul className="list-disc list-inside text-muted-foreground">
                                {data.portfolio_analysis.rebalance_recommendations.map((rec, index) => (
                                    <li key={index}>{rec}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted-foreground">No specific rebalancing recommendations at this time.</p>
                        )}
                    </div>
                    <div>
                        <h3 className="font-semibold text-lg">Summary:</h3>
                        <p className="text-muted-foreground whitespace-pre-line">{data.portfolio_analysis.summary}</p>
                    </div>
                </CardContent>
            </Card>

            <Card className="md:col-span-12 overflow-hidden rounded-md border border-emerald-600/30 shadow-lg h-full bg-gradient-to-br from-slate-950 to-slate-900/80">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-green-500"></div>
              <CardHeader className="p-4 md:p-5 border-b border-border/10 bg-gradient-to-r from-slate-950 to-slate-900">
                <CardTitle className="text-lg md:text-2xl">Token Holdings ({data.tokens.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-5 bg-gradient-to-br from-slate-950 to-slate-900/80">
                <div className="overflow-x-auto -mx-3 md:mx-0 pb-2">
                <Table className="w-full min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px] md:w-[50px]"></TableHead>
                      <TableHead>Token</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Price (USD)</TableHead>
                      <TableHead className="text-right">Value (USD)</TableHead>
                      <TableHead className="text-right">% of Portfolio</TableHead>
                      <TableHead className="text-center">Contract</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tokens.map((token) => (
                      <TableRow key={token.contract_address || token.symbol}>
                        <TableCell>
                          {token.logo_url ? (
                            <img src={token.logo_url} alt={token.token_name} className="h-8 w-8 rounded-full" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs">
                              {token.symbol.substring(0,3)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{token.token_name}</div>
                          <div className="text-xs text-muted-foreground">{token.symbol} {token.is_native && "(Native)"}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono text-sm break-all">
                            {token.quantity ? token.quantity.toLocaleString() : '0'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono text-sm break-all">
                            ${token.current_price_usd ? token.current_price_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) : '0.00'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <div className="font-mono text-sm break-all">
                            ${token.total_value_usd ? token.total_value_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono text-sm">
                            {token.percentage_of_portfolio ? token.percentage_of_portfolio.toFixed(2) : '0.00'}%
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {token.contract_address && (
                            <a 
                                href={`${getBlockchainExplorer(chainId)}/token/${token.contract_address}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-600 hover:underline"
                            >
                                <ExternalLink className="h-4 w-4 inline-block"/>
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {isMobile && (
        <div className="relative z-20">
          <MobileNav />
        </div>
      )}
    </AppLayout>
  );
};

// Helper to get blockchain explorer URL
const getBlockchainExplorer = (chainId: string): string => {
  switch (chainId) {
    case 'ethereum': return 'https://etherscan.io';
    case 'bsc': return 'https://bscscan.com';
    case 'polygon': return 'https://polygonscan.com';
    case 'solana': return 'https://solscan.io'; // Note: Solscan token URLs might differ
    case 'avalanche': return 'https://snowtrace.io';
    case 'arbitrum': return 'https://arbiscan.io';
    case 'optimism': return 'https://optimistic.etherscan.io';
    default: return 'https://etherscan.io'; // Fallback
  }
}

export default WalletAnalyzer;
